// Shared types and helpers for the External Bookings tracker (off-platform reservations).
//
// External bookings live in `public.external_bookings` and are NEVER mixed with
// `public.bookings` (no commissions, no payments). They are a tracking-only tool.
//
// This module also exposes the `UnifiedVendorEvent` model and the load-per-day
// helpers used by the AI Turnover Scheduler engine (Sprint 2.5). Keep the
// shape stable: the future server-side engine (Epic 5) will mirror it.

import { format } from "date-fns";
import type { Booking } from "@/types";

export type ExternalSchedulingMode = "exact" | "window" | "date_only" | "flexible";

export type ExternalBookingStatus = "planned" | "in_progress" | "completed" | "cancelled";

export interface ExternalBookingRow {
  id: string;
  company_id: string;
  created_by_user_id: string;
  title: string | null;
  address: string | null;
  guest_name: string | null;
  guest_phone: string | null;
  guest_email: string | null;
  service_label: string | null;
  service_id: string | null;
  scheduling_mode: ExternalSchedulingMode;
  start_at: string | null;
  end_at: string | null;
  all_day: boolean;
  duration_minutes: number | null;
  guests: number | null;
  has_pets: boolean | null;
  has_kids: boolean | null;
  amount: number | null;
  currency: string | null;
  status: ExternalBookingStatus;
  notes: string | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

/** Normalised event for the unified calendar / list. */
export interface UnifiedCalendarEvent {
  id: string;
  source: "platform" | "external";
  /** Primary day used to bucket on the calendar grid. */
  day: Date;
  /** Optional time string "HH:mm" (or empty if all-day / flexible). */
  timeLabel: string;
  /** Visible title (customer name + service / property). */
  title: string;
  subtitle?: string;
  /** Hex color for the dot on the calendar / chip. */
  color: string;
  /** "exact" | "window" | "date_only" | "flexible" | "platform". */
  modeLabel: string;
  /** Status label localised. */
  statusLabel: string;
  /** Original underlying object - useful for the detail panel. */
  raw: Booking | ExternalBookingRow;
}

const STATUS_COLORS: Record<ExternalBookingStatus, string> = {
  planned: "#3b82f6",      // primary blue
  in_progress: "#f59e0b",  // amber
  completed: "#22c55e",    // accent-green
  cancelled: "#94a3b8",    // slate-400
};

export const PLATFORM_EVENT_COLOR = "#1d4ed8";
export const EXTERNAL_EVENT_COLOR = "#10b981";

export function externalEventColor(row: Pick<ExternalBookingRow, "status" | "color">): string {
  if (row.color && /^#[0-9a-f]{6}$/i.test(row.color)) return row.color;
  return STATUS_COLORS[row.status] ?? EXTERNAL_EVENT_COLOR;
}

export function safeDate(input: unknown): Date | null {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(String(input));
  return isNaN(d.getTime()) ? null : d;
}

export function modeLabelIt(mode: ExternalSchedulingMode): string {
  switch (mode) {
    case "exact":
      return "Orario esatto";
    case "window":
      return "Finestra checkout/check-in";
    case "date_only":
      return "Solo giorno";
    case "flexible":
      return "Flessibile";
  }
}

export function statusLabelIt(status: ExternalBookingStatus): string {
  switch (status) {
    case "planned":
      return "Pianificato";
    case "in_progress":
      return "In corso";
    case "completed":
      return "Completato";
    case "cancelled":
      return "Annullato";
  }
}

export function formatTimeRange(row: ExternalBookingRow, t?: (key: string) => string): string {
  if (row.scheduling_mode === "flexible") return t ? t("vendor.tracker.mode.flexible") : "Flexible";
  const s = safeDate(row.start_at);
  const e = safeDate(row.end_at);
  if (row.scheduling_mode === "exact" && s) return format(s, "HH:mm");
  if (row.scheduling_mode === "date_only" && s) return t ? t("vendor.tracker.time.all_day") : "All day";
  if (row.scheduling_mode === "window" && s && e) return `${format(s, "HH:mm")} → ${format(e, "HH:mm")}`;
  if (s && e) return `${format(s, "HH:mm")} → ${format(e, "HH:mm")}`;
  if (s) return format(s, "HH:mm");
  return "—";
}

/** Returns the "primary day" used to render an external booking on the calendar grid. */
export function externalPrimaryDay(row: ExternalBookingRow): Date | null {
  // For window/date_only we show the booking on every day inside the window
  // at the call site by calling externalDays(row).
  return safeDate(row.start_at);
}

/** All days a row should appear on (window-aware, capped to a sane range). */
export function externalDays(row: ExternalBookingRow): Date[] {
  if (row.scheduling_mode === "flexible") return [];
  const s = safeDate(row.start_at);
  if (!s) return [];
  if (row.scheduling_mode === "exact") return [stripTime(s)];
  const e = safeDate(row.end_at) ?? s;
  // Cap at 14 days as a defensive measure.
  const days: Date[] = [];
  const cur = stripTime(s);
  const end = stripTime(e);
  let i = 0;
  while (cur.getTime() <= end.getTime() && i < 14) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
    i += 1;
  }
  return days;
}

function stripTime(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// ---------------------------------------------------------------------------
// Unified vendor load model (Sprint 2.5)
// ---------------------------------------------------------------------------
//
// `UnifiedVendorEvent` is the engine-level shape: one entry per (event, day)
// touch. A platform booking generates one entry on its `scheduled_date` (or
// `window_start_at` if not yet scheduled). An external `window` / `date_only`
// booking generates one entry per day inside the window. `flexible` external
// bookings without `start_at` generate ZERO entries by design — they live in
// the "unscheduled" queue, not in the load count.

export type UnifiedSchedulingPrecision = "exact" | "window" | "date_only" | "flexible" | "platform";

export interface UnifiedVendorEvent {
  /** Stable unique key, suitable for React lists. */
  key: string;
  source: "platform" | "external";
  /** Original row id (multiple events can share the same id when the row spans days). */
  id: string;
  company_id: string;
  /** Calendar day (00:00 local) the vendor is occupied. */
  day: Date;
  /** Datetime when the executable window starts on this day, when known. */
  range_start_at: string | null;
  /** Datetime when the executable window ends on this day, when known. */
  range_end_at: string | null;
  /** Used to evaluate "tight_window" and "urgent" risk flags. */
  window_start_at: string | null;
  window_end_at: string | null;
  /** Used to evaluate `same_day_turnover`. */
  checkout_at: string | null;
  checkin_at: string | null;
  scheduling_precision: UnifiedSchedulingPrecision;
  service_id: string | null;
  /** Status normalised across sources (used to filter cancelled rows out of load). */
  status: string;
  /** Original underlying object — keeps room for richer detail panels. */
  raw: Booking | ExternalBookingRow;
}

/** Returns "yyyy-MM-dd" for a Date in local time. */
export function dayKey(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

/**
 * Build `UnifiedVendorEvent` entries from platform bookings and external rows.
 *
 * Ground rules:
 * - cancelled rows are excluded from the load completely (they don't contribute
 *   to capacity pressure).
 * - external `flexible` rows (without start_at) are excluded too — they are
 *   surfaced separately as "Da pianificare".
 */
/**
 * The `Booking` interface from `@/types` does not yet declare all the
 * scheduling-related fields we read on the supply side (window_*, checkin_at,
 * checkout_at, scheduled_date, service_id, status). We treat each row as the
 * union of the canonical `Booking` and the platform-specific fields.
 */
type PlatformBookingFields = Booking & Partial<{
  status: string;
  scheduled_date: string | null;
  window_start_at: string | null;
  window_end_at: string | null;
  checkin_at: string | null;
  checkout_at: string | null;
  service_id: string | null;
}>;

export function buildUnifiedEvents(opts: {
  companyId: string;
  bookings: Booking[];
  externalRows: ExternalBookingRow[];
}): UnifiedVendorEvent[] {
  const out: UnifiedVendorEvent[] = [];

  for (const raw of opts.bookings) {
    const b = raw as PlatformBookingFields;
    const status = String(b.status || "").toLowerCase();
    if (status === "cancelled") continue;
    const dayIso =
      b.scheduled_date ||
      (b.window_start_at || "").slice(0, 10) ||
      (b.checkin_at || "").slice(0, 10);
    const day = safeDate(dayIso);
    if (!day) continue;
    out.push({
      key: `platform-${b.id}-${dayKey(day)}`,
      source: "platform",
      id: String(b.id),
      company_id: opts.companyId,
      day: stripTime(day),
      range_start_at: b.window_start_at ?? null,
      range_end_at: b.window_end_at ?? null,
      window_start_at: b.window_start_at ?? null,
      window_end_at: b.window_end_at ?? null,
      checkout_at: b.checkout_at ?? null,
      checkin_at: b.checkin_at ?? null,
      scheduling_precision: "platform",
      service_id: b.service_id ?? null,
      status,
      raw: b,
    });
  }

  for (const ext of opts.externalRows) {
    if (ext.status === "cancelled") continue;
    if (ext.scheduling_mode === "flexible") continue;
    const days = externalDays(ext);
    if (days.length === 0) continue;
    for (const d of days) {
      out.push({
        key: `external-${ext.id}-${dayKey(d)}`,
        source: "external",
        id: ext.id,
        company_id: opts.companyId,
        day: d,
        range_start_at: ext.start_at,
        range_end_at: ext.end_at,
        window_start_at: ext.start_at,
        window_end_at: ext.end_at,
        checkout_at: ext.start_at,
        checkin_at: ext.end_at,
        scheduling_precision: ext.scheduling_mode,
        service_id: ext.service_id,
        status: ext.status,
        raw: ext,
      });
    }
  }

  return out;
}

/** Bucket events by `dayKey(day)` for fast O(1) lookup. */
export function groupUnifiedEventsByDay(events: UnifiedVendorEvent[]): Map<string, UnifiedVendorEvent[]> {
  const map = new Map<string, UnifiedVendorEvent[]>();
  for (const e of events) {
    const k = dayKey(e.day);
    const arr = map.get(k);
    if (arr) {
      arr.push(e);
    } else {
      map.set(k, [e]);
    }
  }
  return map;
}

/** Convenience wrapper: returns just the count for a given day. */
export function dayLoadCount(events: UnifiedVendorEvent[], day: Date): number {
  const k = dayKey(stripTime(day));
  let count = 0;
  for (const e of events) {
    if (dayKey(e.day) === k) count += 1;
  }
  return count;
}
